import { fetchDashboardCards, fetchDashboardGraphs } from '@/api/dashboard'
import { mapGetters } from 'vuex'
import { TIME_PERIODS } from '@/store/time-periods'
import userMixin from '@/mixins/user'
import moment from 'moment'

// Constants
export const METRIC_KEYS = Object.freeze([
  'views',
  'leads',
  'partial_leads',
  'conversion_rate',
  'completion_time'
])

export const METRIC_CARD_CONFIG = Object.freeze([
  {
    key: 'views',
    title: 'Views',
    icon: 'mdi-eye-outline',
    color: 'brand-color',
    supportsActivation: false,
    activationRoute: null,
    valueFormatter: null
  },
  {
    key: 'leads',
    title: 'Submissions',
    icon: 'mdi-account-group',
    color: 'success-color',
    supportsActivation: false,
    activationRoute: null,
    valueFormatter: null
  },
  {
    key: 'partial_leads',
    title: 'Partial Submissions',
    icon: 'mdi-account-supervisor-circle-outline',
    color: 'danger-color',
    supportsActivation: true,
    activationRoute: { name: 'partialleads' },
    valueFormatter: null
  },
  {
    key: 'conversion_rate',
    title: 'Conversion Rate',
    icon: 'mdi-chart-line',
    color: 'info',
    supportsActivation: false,
    activationRoute: null,
    valueFormatter: (value) => `${value}%`
  },
  {
    key: 'completion_time',
    title: 'Completion Time',
    icon: 'mdi-clock-outline',
    color: 'accent-color',
    supportsActivation: false,
    activationRoute: null,
    valueFormatter: null
  }
])

export const FILTER_VALUES = Object.freeze({
  FORM_ALL: 'all'
})
const DASHBOARD_STAT_DATE_FORMAT = 'YYYY-MM-DD'

const METRIC_RESPONSE_FIELDS = Object.freeze({
  views: 'visits',
  leads: 'leads',
  partial_leads: 'partial_leads',
  conversion_rate: 'conversion_rate',
  completion_time: 'completion_time'
})

const GRAPH_POINT_FIELDS = Object.freeze({
  views: {
    label: 'visits_created_at',
    value: 'visits',
    source: 'visits'
  },
  leads: {
    label: 'leads_created_at',
    value: 'leads',
    source: 'leads'
  },
  partial_leads: {
    label: 'partial_leads_created_at',
    value: 'partial_leads',
    source: 'partial_leads'
  },
  conversion_rate: {
    label: 'conversion_rate_created_at',
    value: 'conversion_rate',
    source: 'conversion_rate'
  },
  completion_time: {
    label: 'completion_time_created_at',
    value: 'completion_time',
    source: 'completion_time'
  }
})

export default {
  mixins: [userMixin],

  data() {
    return {
      isRefreshing: false,
      cardsLoading: false,
      loadFailed: false,
      metrics: {
        views: null,
        leads: null,
        partial_leads: null,
        conversion_rate: null,
        completion_time: null
      },
      metricStatus: {
        views: { loading: false, loaded: false },
        leads: { loading: false, loaded: false },
        partial_leads: { loading: false, loaded: false },
        conversion_rate: { loading: false, loaded: false },
        completion_time: { loading: false, loaded: false }
      },
      debug: false,
      lastPayload: null,
      filterValues: {
        period: TIME_PERIODS.WEEK_TO_DATE,
        compare: '',
        custom_start_date: null,
        custom_end_date: null,
        form: FILTER_VALUES.FORM_ALL
      },
      draftFilterValues: {
        period: TIME_PERIODS.WEEK_TO_DATE,
        compare: '',
        custom_start_date: null,
        custom_end_date: null,
        form: FILTER_VALUES.FORM_ALL
      },
      frozenComparisonActive: false
    }
  },

  computed: {
    ...mapGetters(['user']),

    userName() {
      const fullName = this.user?.name
      if (!fullName) return null
      return fullName.split(' ')[0] || null
    },

    shouldShowOnboarding() {
      const onboardingData = this.user?.onboardingData || this.user?.onboarding_data
      const hasOnboardingData = !!onboardingData && Object.keys(onboardingData).length > 0
      return !!this.user && !this.isAdmin && !hasOnboardingData
    },

    isComparisonActive() {
      if (this.cardsLoading) {
        return this.frozenComparisonActive
      }
      return !!this.filterValues?.compare
    },

    metricCardConfig() {
      return METRIC_CARD_CONFIG
    }
  },

  watch: {
    user: {
      immediate: true,
      handler(user) {
        if (!user?.id) return
        this.$nextTick(() => {
          this.loadDashboard(this.filterValues)
        })
      }
    }
  },

  mounted() {
    if (this.user?.id) {
      this.$nextTick(() => {
        this.loadDashboard(this.filterValues)
      })
    }
  },

  methods: {
    mergeFilterValues(current, incoming = {}) {
      return {
        period: incoming.period || current.period,
        compare: (incoming.compare !== undefined) ? incoming.compare : current.compare,
        custom_start_date: incoming.custom_start_date || null,
        custom_end_date: incoming.custom_end_date || null,
        form: incoming.form || FILTER_VALUES.FORM_ALL
      }
    },

    setMetricStatusForAll({ loading, loaded } = {}) {
      METRIC_KEYS.forEach(metricKey => {
        const status = this.metricStatus?.[metricKey]
        if (!status) return
        if (loading !== undefined) status.loading = loading
        if (loaded !== undefined) status.loaded = loaded
      })
    },

    onFiltersDraftChange(filterData) {
      this.draftFilterValues = this.mergeFilterValues(this.draftFilterValues, filterData)
    },

    onFiltersApply(filterData) {
      this.filterValues = this.mergeFilterValues(this.filterValues, filterData)
      this.loadDashboard(this.filterValues)
    },

    onFiltersRefresh() {
      this.loadDashboard(this.filterValues)
    },

    upsertMetricData(metricKey, incoming = null, comparisonEnabled = false) {
      if (!metricKey) return
      if (!incoming || typeof incoming !== 'object') return

      const prev = this.metrics?.[metricKey]
      const prevObj = (prev && typeof prev === 'object') ? prev : {}
      const merged = { ...prevObj, ...incoming }
      const next = comparisonEnabled ? merged : this.stripComparisonFields(merged)

      this.metrics[metricKey] = next
    },

    stripComparisonFields(metricObj = {}) {
      const {
        total_percentage_change,
        compare_total,
        compareTotal,
        previous_total,
        previousTotal,
        prev_total,
        prevTotal,
        ...rest
      } = metricObj || {}

      if (rest && typeof rest === 'object' && rest.compare && typeof rest.compare === 'object') {
        return { ...rest, compare: false }
      }
      return rest
    },

    toSafeNumber(value) {
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : 0
    },
    toLocalDashboardDate(value) {
      if (!value) return null
      const utcDate = moment.utc(value, [
        'YYYY-MM-DD HH:mm:ss',
        DASHBOARD_STAT_DATE_FORMAT,
        moment.ISO_8601
      ], true)
      return (utcDate.isValid() ? utcDate : moment.utc(value)).local().format(DASHBOARD_STAT_DATE_FORMAT)
    },
    toConversionRate(row = {}) {
      if (row?.conversion_rate !== undefined && row?.conversion_rate !== null) {
        return this.toSafeNumber(row.conversion_rate)
      }
      const conversions = this.toSafeNumber(row?.conversions)
      const visitors = this.toSafeNumber(row?.visitors)
      if (visitors <= 0) return 0
      return Number(((conversions / visitors) * 100).toFixed(2))
    },

    calculatePercentageChange(currentValue, previousValue) {
      const current = this.toSafeNumber(currentValue)
      const previous = this.toSafeNumber(previousValue)
      if (previous === 0) {
        return current === 0 ? 0 : 100
      }
      return Number((((current - previous) / Math.abs(previous)) * 100).toFixed(2))
    },

    buildCardMetrics(cardData = {}, comparisonEnabled = false) {
      const current = cardData?.current || {}
      const previous = comparisonEnabled ? (cardData?.previous || {}) : {}
      const result = {}

      METRIC_KEYS.forEach(metricKey => {
        const sourceField = METRIC_RESPONSE_FIELDS[metricKey]
        const total = this.toSafeNumber(current?.[sourceField])
        const metricPayload = { total }

        if (comparisonEnabled) {
          const hasPreviousValue = Object.prototype.hasOwnProperty.call(previous, sourceField)
          const compareTotal = hasPreviousValue ? this.toSafeNumber(previous[sourceField]) : 0
          metricPayload.compareTotal = compareTotal
          metricPayload.total_percentage_change = this.calculatePercentageChange(total, compareTotal)
        }

        result[metricKey] = metricPayload
      })

      return result
    },

    buildGraphPoint(metricKey, row = {}) {
      const rawDate = row?.stat_date || row?.date || row?.created_at || null
      const statDate = this.toLocalDashboardDate(rawDate)
      const config = GRAPH_POINT_FIELDS[metricKey] || GRAPH_POINT_FIELDS.completion_time
      const value = metricKey === 'conversion_rate'
        ? this.toConversionRate(row)
        : this.toSafeNumber(row?.[config.source])
      return {
        [config.label]: statDate,
        [config.value]: value
      }
    },

    buildGraphMetrics(graphData = {}, comparisonEnabled = false) {
      const currentRows = Array.isArray(graphData?.current) ? graphData.current : []
      const previousRows = comparisonEnabled && Array.isArray(graphData?.previous) ? graphData.previous : []
      const result = {}

      METRIC_KEYS.forEach(metricKey => {
        const value = currentRows.map(row => this.buildGraphPoint(metricKey, row))
        const metricPayload = { value }

        if (comparisonEnabled) {
          metricPayload.compare = {
            value: previousRows.map(row => this.buildGraphPoint(metricKey, row))
          }
        }

        result[metricKey] = metricPayload
      })

      return result
    },

    async loadDashboard(filters = this.filterValues) {
      if (this.isRefreshing) return
      if (!this.user?.id || !this.$refs.dashboardFilters) return
      this.isRefreshing = true
      this.cardsLoading = true
      this.loadFailed = false

      try {
        const payload = this.$refs.dashboardFilters.buildPayload(this.user.id, filters)
        this.lastPayload = payload
        const comparisonEnabled = !!filters?.compare
        this.setMetricStatusForAll({ loading: true })

        const cardRes = await fetchDashboardCards(payload)
        const cardMetrics = this.buildCardMetrics(cardRes?.data?.data, comparisonEnabled)
        METRIC_KEYS.forEach(metricKey => {
          this.upsertMetricData(metricKey, cardMetrics[metricKey], comparisonEnabled)
        })
        this.setMetricStatusForAll({ loading: false, loaded: true })

        this.cardsLoading = false
        this.frozenComparisonActive = comparisonEnabled

        const graphRes = await fetchDashboardGraphs(payload)
        const graphMetrics = this.buildGraphMetrics(graphRes?.data?.data, comparisonEnabled)
        METRIC_KEYS.forEach(metricKey => {
          this.upsertMetricData(metricKey, graphMetrics[metricKey], comparisonEnabled)
        })

        this.$nextTick(() => {
          METRIC_KEYS.forEach(metricKey => {
            this.$refs.dashboardGraph?.renderOrRefreshChart(metricKey)
          })
        })
      } catch (error) {
        console.error('❌ Dashboard Error:', error)
        // A failed request left `loaded: true` with whatever metrics were already in state -
        // for a brand-new account that's nothing, so the cards just sat there looking stuck
        // instead of showing 0 like a genuinely empty account would. Re-run the same
        // zero-payload path buildCardMetrics already produces for an empty response, so
        // failure renders identically to "no data yet" instead of a distinct stuck state.
        const zeroMetrics = this.buildCardMetrics({}, false)
        METRIC_KEYS.forEach(metricKey => {
          this.upsertMetricData(metricKey, zeroMetrics[metricKey], false)
        })
        this.setMetricStatusForAll({ loading: false, loaded: true })
        this.cardsLoading = false
        this.frozenComparisonActive = false
        this.loadFailed = true
      } finally {
        this.isRefreshing = false
      }
    }
  }
}
